import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataRetailersDTO } from "@src/domain/dto/masterData";
import type { MasterDataRetailer } from "@src/domain/entities/masterData";
import { GetMasterDataRetailersUseCase } from "@src/application/useCases/masterData";
import { MasterDataRetailerRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataListQuery } from "./masterData/useMasterDataListQuery";

interface MasterDataRetailersDataContextValue {
  brandKey: string;
  retailers: MasterDataRetailer[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const MasterDataRetailersDataContext =
  createContext<MasterDataRetailersDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataRetailers(): MasterDataRetailersDataContextValue {
  const context = useContext(MasterDataRetailersDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataRetailers must be used within MasterDataRetailersDataProvider",
    );
  }
  return context;
}

interface MasterDataRetailersDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function MasterDataRetailersDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<MasterDataRetailersDataProviderProps>) {
  const getRetailersUseCase = useMemo(
    () => new GetMasterDataRetailersUseCase(new MasterDataRetailerRepository()),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getRetailersUseCase.execute(
        new GetMasterDataRetailersDTO({
          search: args.search,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getRetailersUseCase],
  );

  const {
    items: retailers,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useMasterDataListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load master data retailers",
    cacheScope: "retailers",
    fetchPage,
  });

  const value = useMemo(
    (): MasterDataRetailersDataContextValue => ({
      brandKey,
      retailers,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      retailers,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <MasterDataRetailersDataContext.Provider value={value}>
      {children}
    </MasterDataRetailersDataContext.Provider>
  );
}
